import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Sparkles, Send, Loader2, X, Wand2, ArrowRight, Check, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const QUICK = [
  "Cosa manca nella mia guida?",
  "Migliora le istruzioni di check-in",
  "Crea le istruzioni di check-out",
  "Scrivi le regole della casa",
  "Suggerisci FAQ utili",
  "Rendi i miei testi più professionali",
];

// Fields the assistant can improve inline (host approves before applying).
const IMPROVABLE = {
  welcome: { kind: "welcome_message", label: "Messaggio di benvenuto", get: (p) => p.welcome_message },
  checkin: { kind: "checkin_instructions", label: "Istruzioni check-in", get: (p) => p.checkin?.instructions },
  checkout: { kind: "checkout_instructions", label: "Istruzioni check-out", get: (p) => p.checkout?.instructions },
};

export function HostAssistant({ open, onClose, prop, onApplyImprove }) {
  const navigate = useNavigate();
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sug, setSug] = useState(null); // { key, current, text, loading }
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 1e6, behavior: "smooth" }); }, [msgs, loading, sug]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput(""); setSug(null);
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const { data } = await api.post(`/properties/${prop.id}/host-assistant`, { message: q, session_id: sessionRef.current });
      sessionRef.current = data.session_id;
      setMsgs((m) => [...m, { role: "assistant", content: data.reply, actions: data.actions || [] }]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Assistente non disponibile");
      setMsgs((m) => [...m, { role: "assistant", content: "Ops, riprova tra poco.", actions: [] }]);
    } finally { setLoading(false); }
  };

  const suggest = async (key) => {
    setSug({ key, loading: true, current: IMPROVABLE[key].get(prop) || "", text: "" });
    try {
      const { data } = await api.post("/ai/generate", {
        kind: IMPROVABLE[key].kind,
        context: { language: "it", name: prop.name, city: prop.basic?.city, address: prop.basic?.address },
      });
      setSug({ key, loading: false, current: IMPROVABLE[key].get(prop) || "", text: data.text });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generazione non riuscita");
      setSug(null);
    }
  };

  const onAction = (a) => {
    if (a.type === "improve" && IMPROVABLE[a.key]) return suggest(a.key);
    navigate(`/dashboard/properties/${prop.id}/edit`);
    onClose();
  };

  const applySug = async () => {
    const ok = await onApplyImprove(sug.key, sug.text);
    if (ok) { setMsgs((m) => [...m, { role: "assistant", content: `✓ Ho aggiornato: ${IMPROVABLE[sug.key].label}.`, actions: [] }]); setSug(null); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl h-[88vh] flex flex-col p-0" data-testid="host-assistant">
        <SheetTitle className="sr-only">Assistente Host AI</SheetTitle>
        <div className="flex items-center gap-3 px-4 py-3.5 bg-blue-600 text-white shrink-0 rounded-t-3xl">
          <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><Sparkles className="w-4.5 h-4.5" /></div>
          <div className="flex-1 min-w-0"><p className="font-display font-bold leading-tight">Assistente Host AI</p><p className="text-[11px] text-blue-100">Ti aiuta a creare e migliorare la tua guida</p></div>
          <button onClick={onClose} data-testid="host-assistant-close" className="w-8 h-8 grid place-items-center rounded-full bg-white/15"><X className="w-4 h-4" /></button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {msgs.length === 0 && (
            <div className="text-center pt-4">
              <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 grid place-items-center mx-auto"><Wand2 className="w-7 h-7" /></div>
              <p className="mt-3 font-semibold text-slate-800">Come posso aiutarti oggi?</p>
              <p className="text-sm text-slate-400 mt-1">Conosco solo i dati di «{prop.name}».</p>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-blue-600 text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md"}`}>{m.content}</div>
              {m.actions?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {m.actions.map((a, k) => (
                    <button key={k} onClick={() => onAction(a)} data-testid={`host-action-${a.key}`} className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-700 px-3.5 py-2 text-xs font-semibold active:scale-95 transition-transform">
                      {a.type === "improve" ? <Wand2 className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />}{a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {loading && <div className="bg-slate-100 rounded-2xl rounded-bl-md px-3.5 py-3 w-16"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>}

          {sug && (
            <div className="rounded-2xl border border-blue-200 bg-blue-50/40 p-3.5" data-testid="host-suggestion">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{IMPROVABLE[sug.key].label}</p>
              {sug.current && (<><p className="text-[11px] font-semibold text-slate-400 mt-2">Testo attuale</p><p className="text-sm text-slate-500 line-clamp-3">{sug.current}</p></>)}
              <p className="text-[11px] font-semibold text-blue-600 mt-3">Versione suggerita</p>
              {sug.loading ? <div className="py-3"><Loader2 className="w-4 h-4 animate-spin text-blue-500" /></div>
                : <p className="text-sm text-slate-800 whitespace-pre-wrap">{sug.text}</p>}
              {!sug.loading && (
                <div className="flex gap-2 mt-3">
                  <button onClick={applySug} data-testid="host-apply-suggestion" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-blue-500 text-white text-sm font-semibold py-2.5"><Check className="w-4 h-4" /> Usa questa versione</button>
                  <button onClick={() => suggest(sug.key)} className="px-3 rounded-xl border border-slate-200 text-slate-600"><RefreshCw className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          )}

          {msgs.length === 0 && (
            <div className="pt-2 space-y-2">
              {QUICK.map((q) => (
                <button key={q} onClick={() => send(q)} data-testid="host-quick-prompt" className="w-full text-left rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:border-blue-300 transition-colors">{q}</button>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 p-3 flex items-center gap-2 shrink-0" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Scrivi all'assistente…" data-testid="host-assistant-input" className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-[15px] outline-none focus:border-blue-500" />
          <button onClick={() => send()} disabled={loading || !input.trim()} data-testid="host-assistant-send" className="w-11 h-11 rounded-full bg-blue-500 text-white grid place-items-center disabled:opacity-40"><Send className="w-4 h-4" /></button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
